import type { z } from 'zod';
import { matchExportPreset, sameExportPresetOptions } from './export-presets.js';
import type { ExportPresetDefinition } from './export-presets.js';
import {
  DEFAULT_APPEARANCE_OPTIONS,
  DEFAULT_DRESSING_OPTIONS,
  DEFAULT_PAGE_OPTIONS,
  DEFAULT_TABLE_OPTIONS,
  exportAppearanceOptionsSchema,
  exportDressingOptionsSchema,
  exportPageOptionsSchema,
  exportTableOptionsSchema,
} from './schemas/export.js';
import type { ExportPresetSettings, UserExportPreset } from './schemas/user-export-preset.js';

/**
 * Clears the matrix-specific arrangement fields (`columnWidths`, `columnOrder`,
 * `pageBreaksBefore`): they name palier numbers / dimension UUIDs of ONE
 * matrix, and a personal preset must re-apply on any matrix. Returns a fresh,
 * unaliased object.
 */
export function normalizePresetSettings(settings: ExportPresetSettings): ExportPresetSettings {
  const copy = structuredClone(settings);
  return {
    ...copy,
    table: { ...copy.table, columnWidths: {}, columnOrder: [], pageBreaksBefore: [] },
  };
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/**
 * Repairs one block field by field against the CURRENT schema: a stored value
 * the field schema still accepts is kept, anything else (a retired enum value,
 * an out-of-range number, a missing key) takes the fallback's value. Unknown
 * keys are dropped. Generic over `schema.shape`, so a field added to a block
 * schema later is picked up with no edit here.
 */
function repairBlock<S extends z.ZodObject>(
  schema: S,
  fallback: z.infer<S>,
  raw: unknown,
): { value: z.infer<S>; repaired: boolean } {
  if (!isPlainObject(raw)) {
    return { value: structuredClone(fallback), repaired: true };
  }
  const fallbackRecord: Record<string, unknown> = fallback;
  const shape: Record<string, z.ZodType> = schema.shape;
  let repaired = Object.keys(raw).some((key) => !(key in shape));
  const assembled: Record<string, unknown> = {};
  for (const [key, fieldSchema] of Object.entries(shape)) {
    const parsed = key in raw ? fieldSchema.safeParse(raw[key]) : null;
    if (parsed?.success) {
      assembled[key] = parsed.data;
    } else {
      assembled[key] = structuredClone(fallbackRecord[key]);
      repaired = true;
    }
  }
  const result = schema.safeParse(assembled);
  if (!result.success) {
    // Only reachable if a fallback itself stopped matching its schema — the
    // defaults are the last resort, so hand them back rather than throw.
    return { value: structuredClone(fallback), repaired: true };
  }
  return { value: result.data, repaired };
}

/**
 * Read path for a stored preset: never throws, never drops the row. Per block,
 * per field, keeps what the current schema accepts and fills the rest from the
 * DEFAULT_* blocks. A non-object `raw` (null, a string, an array) yields the
 * four default blocks. `repaired` is true when anything was changed.
 */
export function repairPresetSettings(raw: unknown): {
  settings: ExportPresetSettings;
  repaired: boolean;
} {
  const source = isPlainObject(raw) ? raw : {};
  const extraKeys = Object.keys(source).some(
    (key) => !['page', 'table', 'dressing', 'appearance'].includes(key),
  );
  const page = repairBlock(exportPageOptionsSchema, DEFAULT_PAGE_OPTIONS, source.page);
  const table = repairBlock(exportTableOptionsSchema, DEFAULT_TABLE_OPTIONS, source.table);
  const dressing = repairBlock(
    exportDressingOptionsSchema,
    DEFAULT_DRESSING_OPTIONS,
    source.dressing,
  );
  const appearance = repairBlock(
    exportAppearanceOptionsSchema,
    DEFAULT_APPEARANCE_OPTIONS,
    source.appearance,
  );
  return {
    settings: {
      page: page.value,
      table: table.value,
      dressing: dressing.value,
      appearance: appearance.value,
    },
    repaired:
      !isPlainObject(raw) ||
      extraKeys ||
      page.repaired ||
      table.repaired ||
      dressing.repaired ||
      appearance.repaired,
  };
}

export type ActiveExportPreset =
  | { kind: 'builtin'; id: ExportPresetDefinition['id'] }
  | { kind: 'user'; id: string }
  | null;

/**
 * Which preset the current options correspond to, ignoring the arrangement
 * fields — the built-ins' own rule (`sameExportPresetOptions`), so both kinds
 * match the same way. Built-ins are tried first so they win a tie with an
 * identical personal preset; personal presets follow in list order. `null`
 * means the options were modified away from every preset.
 */
export function matchAnyExportPreset(
  options: ExportPresetSettings,
  userPresets: readonly UserExportPreset[],
): ActiveExportPreset {
  const builtin = matchExportPreset(options);
  if (builtin !== null) {
    return { kind: 'builtin', id: builtin };
  }
  const user = userPresets.find((preset) => sameExportPresetOptions(options, preset.settings));
  return user ? { kind: 'user', id: user.id } : null;
}
